import { useAtom } from '@reatom/npm-react'
import { forwardRef } from 'react'
import styled from 'styled-components'

import { CurrencyIcon } from '@root/entities/currency/ui/currency-icon'
import { PairLink } from '@root/entities/pair/ui/common/pair-link'
import { useFormatDuration } from '@root/shared'
import { MarkerCapShortFormatted } from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'

import type { PairReusedShort } from '../../model/pair'

type PairReusedProps = {
  pair: PairReusedShort
} & Omit<React.HTMLAttributes<HTMLDivElement>, 'onClick'>

export const PairReused = forwardRef<HTMLDivElement, PairReusedProps>(
  ({ pair, ...rest }, ref) => {
    const { t } = useTranslation()
    const { formatDuration } = useFormatDuration()
    const [marketCapUsd] = useAtom(pair.marketCapUsd)
    const [athMarketCap] = useAtom(pair.athMarketCap)

    return (
      <Root ref={ref} data-with-pointer {...rest}>
        <PairBackgroundLink
          pair={{
            address: pair.address,
            network: pair.network,
          }}
        />
        <Content>
          <Main>
            <Icon>
              <CurrencyIcon
                currency={pair.quote}
                size={39}
                withPreview={false}
                // skip pair popover
                // withPairPopover
                // pairAddress={pair.address}
                outline={{
                  type: 'custom',
                  colorString: 'rgba(255, 255, 255, 0.10)',
                }}
              />
            </Icon>
            <Body>
              <Row>
                <Title>{pair.quote.ticker}</Title>
                <MCap>
                  {t('common.mcap-short')}{' '}
                  <MarkerCapShortFormatted mcap={marketCapUsd} />
                </MCap>
              </Row>
              <Row>
                <ATH>
                  {t('common.ath')}{' '}
                  <MarkerCapShortFormatted mcap={athMarketCap} />
                </ATH>
              </Row>
              <Row>
                <Time>
                  {t('explore.developer-tokens.24h-traders')} {pair.traders24h}
                </Time>
                <Time>
                  {formatDuration({
                    startAt: pair.createdAt ?? new Date(0),
                    mode: 'singleUnitShort',
                  })}
                </Time>
              </Row>
            </Body>
          </Main>
        </Content>
      </Root>
    )
  },
)

PairReused.displayName = 'PairReused'

const Root = styled.div`
  border-radius: 7px;
  padding: 1px;
  position: relative;

  &::before {
    content: '';
    display: none;

    position: absolute;
    inset: 0;
    border-radius: 7px;
    opacity: 0;
    transition: opacity 150ms ease;

    &[data-with-pointer='true'] {
      display: block;
    }
  }

  &:hover::before {
    opacity: 1;
  }

  &[data-with-pointer='true'] {
    cursor: pointer;
  }
`

const Content = styled.div`
  border-radius: 6px;
  pointer-events: none;
`
const Main = styled.div`
  display: flex;
  gap: 8px;
  align-items: start;

  font-size: 10px;
  font-weight: 500;
  line-height: normal;
  padding: 7px 7px 6px;
  position: relative;

  border-radius: 6px;
  background: var(--background-4);
  backdrop-filter: blur(1.5px);
`

const Icon = styled.div`
  pointer-events: auto;
`

const Body = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`
const Row = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`
const Title = styled.div`
  font-size: 11px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const MCap = styled.div`
  color: var(--amber-9);
  white-space: nowrap;
`
const ATH = styled.div`
  color: var(--text-12);
  text-align: right;
  white-space: nowrap;
`
const Time = styled.div`
  color: var(--text-9);
`

const PairBackgroundLink = styled(PairLink)`
  position: absolute;
  inset: 0;
`
